// frontend/src/components/homepage/HomeFooter.js
//
// The homepage's footer - the prototype's <footer> (docs/homepage-concepts/
// concept-01b-live-mesh-light.html) carrying the PRODUCTION footer's links
// and behaviours. Styles: src/homepage/homeFooter.css.
//
// It serves EVERY PUBLIC route (owner round 2): AuthLayout renders it in
// place of Footer.js when `navVariant="home"`, the default whenever
// `isPublic` is true - homepage, blog, the landings, the legal pages for a
// signed-out visitor. Authenticated routes keep Footer.js untouched. The two
// files must agree on the LINK SET - the three columns here are Footer.js's `linkGroups` verbatim (same
// hrefs, same order, contact only when signed in, cookie settings opening the
// consent dialog through the same event) - and differ only in design.
//
// DESIGN, from the prototype as the owner corrected it live (round 2): a
// centred brand block - the outlined brand wordmark and the motto, nothing
// else (the prototype's description paragraph, the hairline under the brand
// and its second mono copyright line were all dropped on his word) - then
// three centred columns
// in Footer.js's OWN typography and column gutters (its heading and link
// font, size, weight and colour, its 200px gap - not the prototype's mono
// headings and 96px), then the mono copyright row under a SHORT centred
// hairline (the owner's correction: weaker and narrower than the prototype's
// full-width rule), with the vertical rhythm roughly halved from the
// prototype's. Under 640px the columns wrap (the prototype's rule) and every
// link becomes a 44px tap row.
//
// BRAND ROLLOUT, "Subtler B" (owner, navy audit round 3): a light navy tint
// (4.5%) laid over the page ground, navy text, the wordmark and the motto as
// the outlined brand files through components/brand/Logo.js ("Leadify" navy,
// "AI" #4D7179; the motto in Alice, #3D6F7A) - never a typed wordmark and
// never the old Poppins-italic slogan.
//
// GROUND: the footer sits OUTSIDE `.hl-root` and after AuthLayout's <main>
// (an atomic stacking context), so it carries `hl-root` for the tokens and is
// lifted `position: relative; z-index: 2`. Its ground is the shared footer
// tint (styles/footerBand.css) over the same `transparent -> page` fade that
// homepage.css's `body.hl-page-bg footer[role=contentinfo]` override gives the
// app footer. That override skips this one (`:not(.hl-foot)`), so
// homeFooter.css's `.hl-foot` rule is its only background.
//
// FOLLOW US: under the brand block, the official Facebook / Instagram /
// LinkedIn icons (components/shared/SocialLinks.js, URLs from
// content/officialProfiles.js). They are this footer's own - outside the
// three columns, so the link-set parity with Footer.js (which serves only
// signed-in app routes) is unaffected.
//
// SEO: the footer ships inside the prerendered HTML, so every entry is a real
// anchor. Only the cookie-settings entry is a <button> - an in-place action.

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { OPEN_SETTINGS_EVENT } from "../CookieConsent/CookieConsentBanner";
import Logo from "../brand/Logo";
import SocialLinks from "../shared/SocialLinks";
import "../../styles/footerBand.css";
import "../../homepage/homeFooter.css";

/**
 * The owner-chosen motto; English on both languages, as Footer.js. Rendered as
 * the outlined motto file (Logo variant="motto"), whose alt text is this.
 */
export const MOTTO = "Where Opportunities Find You";

/** The brand block's rendered heights (px): the wordmark, then the motto line. */
export const FOOTER_WORDMARK_HEIGHT = 30;
export const FOOTER_MOTTO_HEIGHT = 14;

/**
 * @param {object} props
 * @param {boolean} [props.isHebrew] AuthLayout's language.
 */
export default function HomeFooter({ isHebrew = true }) {
  const currentYear = new Date().getFullYear();
  const { user } = useAuth();

  // Footer.js's strings, verbatim.
  const text = {
    blog: isHebrew ? "בלוג" : "Blog",
    supporterNetwork: isHebrew ? "רשת תומכים" : "Supporter Network",
    businessWebsite: isHebrew ? "בניית אתר לעסק" : "Business Website",
    facebookGroupScanning: isHebrew ? "סריקת קבוצות פייסבוק" : "Facebook Group Scanning",
    termsOfUse: isHebrew ? "תנאי שימוש" : "Terms of Use",
    privacyPolicy: isHebrew ? "מדיניות פרטיות" : "Privacy Policy",
    cookiePolicy: isHebrew ? "מדיניות עוגיות" : "Cookie Policy",
    cookieSettings: isHebrew ? "הגדרות עוגיות" : "Cookie Settings",
    accessibilityStatement: isHebrew ? "הצהרת נגישות" : "Accessibility Statement",
    contactUs: isHebrew ? "צור קשר" : "Contact Us",
    allRightsReserved: isHebrew ? "כל הזכויות שמורות" : "All rights reserved",
    groupProduct: isHebrew ? "מוצר" : "Product",
    groupSupport: isHebrew ? "תמיכה" : "Support",
    groupLegal: isHebrew ? "משפטי" : "Legal",
    footerNav: isHebrew ? "ניווט בתחתית האתר" : "Footer navigation",
  };

  // Footer.js's `linkGroups`, verbatim - order included.
  const linkGroups = [
    {
      id: "hl-foot-group-product",
      title: text.groupProduct,
      items: [
        { href: "/blog", label: text.blog },
        { href: "/supporter-network", label: text.supporterNetwork },
        { href: "/business-website", label: text.businessWebsite },
        { href: "/facebook-group-scanning", label: text.facebookGroupScanning },
      ],
    },
    {
      id: "hl-foot-group-support",
      title: text.groupSupport,
      items: [
        ...(user ? [{ href: "/contact", label: text.contactUs }] : []),
        {
          onClick: () => window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT)),
          label: text.cookieSettings,
        },
        { href: "/accessibility", label: text.accessibilityStatement },
      ],
    },
    {
      id: "hl-foot-group-legal",
      title: text.groupLegal,
      items: [
        { href: "/terms", label: text.termsOfUse },
        { href: "/cookies", label: text.cookiePolicy },
        { href: "/privacy", label: text.privacyPolicy },
      ],
    },
  ];

  return (
    <footer className="hl-root hl-foot" role="contentinfo" aria-label="Site footer">
      <div className="hl-foot-wrap">
        <div className="hl-foot-brand">
          <Logo variant="wordmark" height={FOOTER_WORDMARK_HEIGHT} className="hl-foot-wordmark" />
          <Logo variant="motto" height={FOOTER_MOTTO_HEIGHT} className="hl-foot-slogan" />
        </div>

        {/* The follow-us icons, centred under the brand block (a sibling of
            it - the brand block stays the two outlined images only). */}
        <SocialLinks isHebrew={isHebrew} className="hl-foot-social" />

        <nav className="hl-foot-grid" aria-label={text.footerNav}>
          {linkGroups.map((group) => (
            <div className="hl-foot-col" key={group.id}>
              <h4 id={group.id}>{group.title}</h4>
              <ul aria-labelledby={group.id}>
                {group.items.map((item) => (
                  <li key={item.href || item.label}>
                    {item.href ? (
                      <RouterLink to={item.href}>{item.label}</RouterLink>
                    ) : (
                      <button type="button" onClick={item.onClick}>
                        {item.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="hl-foot-copy">
          {/* ONE template string, never adjacent JSX text nodes. React renders
              siblings as separate text nodes with <!-- --> separators, the
              prerendered HTML carries them, and React 19 counts the mismatch as
              a hydration failure and regenerates the WHOLE root - so this one
              line was re-rendering the entire prerendered page on every load. */}
          <span>{`© ${currentYear} LeadifyAI. ${text.allRightsReserved}`}</span>
        </div>
      </div>
    </footer>
  );
}
